import { Link } from 'react-router-dom';
import { shell } from '../../shell/shellClasses';
import { formatInt, formatMoney, formatPct } from '../daily-brief/format';
import { MiniSparkline } from './MiniSparkline';
import type { ExecutiveKpi, KpiStatus } from './types';

function formatValue(kpi: ExecutiveKpi): string {
  if (kpi.value == null) return 'N/A';
  if (kpi.format === 'money') return formatMoney(kpi.value, kpi.currency);
  if (kpi.format === 'pct') return `${kpi.value.toFixed(1)}%`;
  if (kpi.format === 'score') return `${Math.round(kpi.value)}`;
  return formatInt(kpi.value);
}

function formatPrior(kpi: ExecutiveKpi): string {
  if (kpi.prior == null) return '—';
  if (kpi.format === 'money') return formatMoney(kpi.prior, kpi.currency);
  if (kpi.format === 'pct') return `${kpi.prior.toFixed(1)}%`;
  if (kpi.format === 'score') return `${Math.round(kpi.prior)}`;
  return formatInt(kpi.prior);
}

const statusLabel: Record<KpiStatus, string> = {
  good: 'Good',
  watch: 'Watch',
  risk: 'Risk',
  na: 'N/A',
};

/** Status is carried by the delta colour, not a separate badge. */
const deltaClass: Record<KpiStatus, string> = {
  good: 'text-[var(--shell-success)]',
  watch: 'text-[var(--shell-warning)]',
  risk: 'text-[var(--shell-danger)]',
  na: shell.muted,
};

export function KpiCard({ kpi }: { kpi: ExecutiveKpi }) {
  const valueText = formatValue(kpi);
  const tip = [kpi.tooltip, kpi.reason].filter(Boolean).join(' ');

  return (
    <Link
      to={kpi.href}
      title={tip}
      aria-label={`${kpi.label}: ${valueText}. Previous ${formatPrior(kpi)}. Change ${formatPct(kpi.pctChange)}. ${statusLabel[kpi.status]}. ${kpi.tooltip}`}
      className={`group flex min-h-[132px] min-w-0 flex-col justify-between rounded-[var(--shell-radius-md)] ${shell.surfaceElevated} p-4 transition-colors duration-[var(--shell-motion-fast)] hover:bg-[var(--shell-hover)] ${shell.focusRing}`}
    >
      <p className={`min-w-0 text-xs font-medium leading-snug ${shell.muted}`}>{kpi.label}</p>

      <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-[var(--shell-fg)]">
        {valueText}
      </p>

      <p className={`mt-1 text-xs font-medium tabular-nums ${deltaClass[kpi.status]}`}>
        {formatPct(kpi.pctChange)}
      </p>

      <div className="mt-3">
        <MiniSparkline values={kpi.sparkline} accent={kpi.accent} label={kpi.label} />
      </div>
    </Link>
  );
}
